import React, { useState } from 'react';
import { Upload, FileText, CheckCircle2, AlertCircle, Download, Loader2, File } from 'lucide-react';

export const FileUpload = ({ project }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const handleFileUpload = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    
    // Simulate upload
    setTimeout(() => {
      setUploadedFiles([
        ...uploadedFiles,
        {
          id: Date.now(),
          name: file.name,
          type,
          size: (file.size / 1024).toFixed(2) + ' KB',
          uploadedAt: new Date().toISOString(),
          status: 'processed'
        }
      ]);
      setUploading(false);
      event.target.value = '';
    }, 2000);
  };

  const uploadCategories = [
    {
      id: 'readiness',
      title: 'Readiness Check',
      description: 'Upload SAP Readiness Check output files',
      icon: CheckCircle2,
      color: 'green',
      accept: '.xml,.json,.txt'
    },
    {
      id: 'atc',
      title: 'ATC Results',
      description: 'ABAP Test Cockpit analysis results',
      icon: FileText,
      color: 'blue',
      accept: '.xml,.html'
    },
    {
      id: 'maintenance',
      title: 'Maintenance Planner',
      description: 'Maintenance Planner export files',
      icon: Download,
      color: 'purple',
      accept: '.xml'
    },
    {
      id: 'custom',
      title: 'Custom Code',
      description: 'Custom ABAP code exports',
      icon: File,
      color: 'orange',
      accept: '.abap,.txt'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl p-8 text-white shadow-xl">
        <div className="flex items-center space-x-3">
          <Upload className="w-10 h-10" />
          <div>
            <h2 className="text-3xl font-bold">File Upload</h2>
            <p className="text-green-100 mt-1">Upload SAP system exports and analysis results</p>
          </div>
        </div>
      </div>

      {/* Upload Categories */}
      <div className="grid lg:grid-cols-2 gap-6">
        {uploadCategories.map((category) => {
          const Icon = category.icon;
          const colorClasses = {
            green: 'from-green-500 to-emerald-600',
            blue: 'from-blue-500 to-cyan-600',
            purple: 'from-purple-500 to-pink-600',
            orange: 'from-orange-500 to-red-600'
          };

          return (
            <div key={category.id} className="bg-white rounded-2xl shadow-lg p-6 border-2 border-dashed border-gray-300 hover:border-blue-400 transition-colors group">
              <div className="flex items-start space-x-4 mb-4">
                <div className={`w-14 h-14 bg-gradient-to-br ${colorClasses[category.color]} rounded-xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform`}>
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{category.title}</h3>
                  <p className="text-sm text-gray-600">{category.description}</p>
                </div>
              </div>

              <label className="block">
                <input
                  type="file"
                  accept={category.accept}
                  onChange={(e) => handleFileUpload(e, category.title)}
                  disabled={uploading}
                  className="hidden"
                />
                <div className="mt-4 px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-2 border-gray-300 rounded-xl cursor-pointer hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 hover:border-blue-400 transition-all text-center group-hover:shadow-md">
                  <Upload className="w-6 h-6 mx-auto mb-2 text-gray-500 group-hover:text-blue-600" />
                  <p className="text-sm font-medium text-gray-700 group-hover:text-blue-700">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {category.accept.split(',').join(', ')}
                  </p>
                </div>
              </label>
            </div>
          );
        })}
      </div>

      {/* Uploading Status */}
      {uploading && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6">
          <div className="flex items-center space-x-3">
            <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
            <div>
              <p className="font-semibold text-blue-900">Uploading file...</p>
              <p className="text-sm text-blue-700 mt-1">Processing and analyzing content</p>
            </div>
          </div>
        </div>
      )}

      {/* Uploaded Files */}
      {uploadedFiles.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Uploaded Files</h3>
          <div className="space-y-3">
            {uploadedFiles.map((file) => (
              <div key={file.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{file.name}</p>
                    <p className="text-sm text-gray-600">{file.type} • {file.size}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium text-green-700">Processed</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-6">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-amber-900 mb-1">File Upload Guidelines</h4>
            <ul className="text-sm text-amber-800 space-y-2 mt-2">
              <li>• <strong>Readiness Check:</strong> Export from transaction RTCCTOOL</li>
              <li>• <strong>ATC Results:</strong> Export ATC findings in XML format</li>
              <li>• <strong>Maintenance Planner:</strong> Download stack XML from SAP Support Portal</li>
              <li>• <strong>Custom Code:</strong> Use SE38/SE80 to export custom programs</li>
            </ul>
            <p className="text-sm text-amber-800 mt-3">
              All uploaded files are automatically analyzed by AI and integrated into your project insights.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
